import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { filter } from 'lodash';

import { TicketViewService } from '../../ticket-view.service';
import { Enums } from '../../../../../configs/Enums';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '../../../../auth/auth.service';

export interface ChangeStatusInterface {
  ticket_id: number;
  ticket_status: number;
  reason: string;
  last_status_change_date: string;
  finish_date: string | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-ticket-status-change',
  templateUrl: './ticket-status-change.component.html',
  styleUrls: ['./ticket-status-change.component.scss'],
  standalone: false,
})
export class TicketStatusChangeComponent implements OnInit, OnDestroy {
  public moment = require('moment');
  public isShowChangeStatus = false;
  public ticketStatuses = null;
  public ticketStatusesFiltered = null;
  public statusTypeSelected = null;
  public statusTypeDate = {
    initDate: null,
    options: null,
    dateObj: null,
    date: null,
    isNewStatusDateInvalid: false,
  };
  public date: null;
  public ticketStatusesLoading = false;
  public ticketStatusesError = null;
  public ticketStatusChangeLoading = false;
  public ticketStatusChangeError = null;
  public comment: string;
  public ticketStatusWarning: string;
  public dateFullFormat = Enums.dates.fullFormat;

  @Input() ticket;

  constructor(
    private ticketViewService: TicketViewService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    let statusId = (this.ticket && this.ticket.ticket_status && this.ticket.ticket_status.id) || null;

    this.isShowChangeStatus = statusId && statusId !== 6 && statusId !== 8;
    this.getTicketStatuses();
  }

  ngOnDestroy() {
    this.statusTypeSelected = null;
    this.comment = null;
  }

  public toggleWithTicketStatusChangeContext(popover: NgbPopover) {
    if (popover.isOpen()) {
      popover.close();
      this.statusTypeSelected = null;
      this.comment = null;
    } else {
      popover.open();
    }
  }

  public getTicketStatuses() {
    this.ticketStatusesLoading = true;
    this.ticketViewService.getTicketsStatuses().subscribe(
      (response) => {
        this.ticketStatuses = response['data'];
        this.ticketStatusesFiltered = filter(this.ticketStatuses, (status: any) => {
          return status.id === 2 || status.id === 6 || status.id === 8;
        });
        this.ticketStatusesError = null;
        this.ticketStatusesLoading = false;
      },
      () => {
        this.ticketStatuses = null;
        this.ticketStatusChangeError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
        this.ticketStatusesLoading = false;
      }
    );
  }

  public changeTicketStatus() {
    let changeStatusData: ChangeStatusInterface = {
      ticket_id: this.ticket.id,
      ticket_status: +this.statusTypeSelected,
      reason: this.comment,
      last_status_change_date: this.ticket.last_status_change_date,
      finish_date: null,
    };

    if (changeStatusData.ticket_status === 8) {
      changeStatusData.finish_date = this.moment(this.statusTypeDate.dateObj.dateMillis).format(this.dateFullFormat);
    }

    this.ticketStatusChangeLoading = true;

    const callEsasSequence = (states: string[]) => {
      if (!states.length) {
        this.ticketViewService.ticketChangeStatus(changeStatusData).subscribe(
          () => {
            this.ticketStatusChangeError = null;
            this.ticketStatusChangeLoading = false;
            window.location.reload();
          },
          (error) => {
            this.ticketStatusChangeError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
            if (error.error && error.error.metadata && error.error.metadata.code === 100030) {
              this.ticketStatusChangeError = 'Введенные данные неактуальны. ' + 'Обновите страницу заявки, чтобы загрузить информацию о последнем статусе.';
            }
            this.ticketStatusChangeLoading = false;
          }
        );
        return;
      }

      const nextState = states.shift();
      this.ticketViewService.changeWorkOrderStatus(this.ticket.work_order_id, nextState).subscribe(
        () => callEsasSequence(states),
        () => {
          this.ticketStatusChangeError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
          this.ticketStatusChangeLoading = false;
        }
      );
    };

    const changeStatusViaEsas = () => {
      this.ticketViewService.ticketChangeStatus(changeStatusData).subscribe(
        () => {
          this.ticketStatusChangeError = null;
          this.ticketStatusChangeLoading = false;
          window.location.reload();
        },
        (error) => {
          this.ticketStatusChangeError = Enums.errorCodes[Enums.errorCodesMaps['BACKEND_ERROR']];
          if (error.error && error.error.metadata && error.error.metadata.code === 100030) {
            this.ticketStatusChangeError = 'Введенные данные неактуальны. ' + 'Обновите страницу заявки, чтобы загрузить информацию о последнем статусе.';
          }
          this.ticketStatusChangeLoading = false;
        }
      );
    };

    if (changeStatusData.ticket_status === 6 && this.ticket.work_order_id) {
      callEsasSequence(['Failed', 'Canceled']);
    } else if (changeStatusData.ticket_status === 8 && this.ticket.work_order_id) {
      callEsasSequence(['InProgress', 'Completed']);
    } else {
      changeStatusViaEsas();
    }
  }

  public onChangeStatusDate(dateObj) {
    this.statusTypeDate.dateObj = dateObj;

    if (dateObj.dateMillis) {
      let a = +new Date(this.ticket.last_status_change_date);
      let b = +new Date(dateObj.dateMillis);
      let c = +new Date();

      if (b >= a && b <= c) {
        this.statusTypeDate.isNewStatusDateInvalid = true;
      } else {
        this.statusTypeDate.isNewStatusDateInvalid = false;

        if (b < a) {
          this.ticketStatusWarning =
            'Время закрытия не может быть ранее времени последнего статуса заявки' + ' (' + this.moment(this.ticket.last_status_change_date).format('DD.MM.YYYY HH:mm') + ').';
        }

        if (b > a && b > c) {
          this.ticketStatusWarning = 'Время закрытия не может быть позже текущего времени.';
        }
      }
    }
  }

  public onStatusTypeChanged(type) {
    if (type === 8) {
      this.statusTypeDate.initDate = new Date();
      setTimeout(() => {
        const date = this.authService.isGRSUser() ? new Date() : this.ticket.last_status_change_date;
        this.statusTypeDate.options = {
          minDate: this.moment(date).startOf('day')._d,
          maxDate: this.moment(date).endOf('day')._d,
        };
      }, 4);
    } else {
      this.statusTypeDate.options = null;
    }
  }
}
